import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Plus, PencilSimple, Trash, YoutubeLogo, Eye, EyeSlash, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * Admin → Landing videos panel.
 * CRUD for the YouTube videos that appear in the "See it in action" homepage section.
 * Editable title, description, sort order, published toggle.
 */
export default function LandingVideosPanel() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | <video object>
  const [analytics, setAnalytics] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [videosRes, analyticsRes] = await Promise.allSettled([
        api.get("/admin/landing-videos"),
        api.get("/admin/landing-videos/analytics?days=30"),
      ]);
      if (videosRes.status === "fulfilled") setVideos(videosRes.value.data || []);
      else throw videosRes.reason;
      const byId = {};
      if (analyticsRes.status === "fulfilled") {
        for (const v of (analyticsRes.value.data?.videos || [])) byId[v.id] = v;
      }
      setAnalytics(byId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    try {
      await api.delete(`/admin/landing-videos/${v.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const togglePublished = async (v) => {
    try {
      await api.put(`/admin/landing-videos/${v.id}`, {
        title: v.title,
        youtube_url: v.youtube_url,
        description: v.description,
        sort_order: v.sort_order,
        published: !v.published,
      });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-landing-videos">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <YoutubeLogo size={20} weight="duotone" className="text-[hsl(var(--primary))]" />
            <h2 className="font-serif text-2xl">Landing-page videos</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            YouTube videos shown in the homepage "See it in action" section. Drag-free sort by the order field.
          </p>
        </div>
        <button onClick={() => setEditing("new")} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="add-video-btn">
          <Plus size={14} weight="bold" /> Add video
        </button>
      </div>

      {loading ? (
        <div className="card-flat p-8 text-center text-muted-foreground">Loading…</div>
      ) : videos.length === 0 ? (
        <div className="card-flat p-12 text-center">
          <YoutubeLogo size={32} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground mb-4">No videos yet. Add a YouTube URL — Estima auto-extracts the video ID.</p>
          <button onClick={() => setEditing("new")} className="btn-primary px-4 py-2 text-sm">Add your first video</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((v) => (
            <div key={v.id} className="card-flat overflow-hidden" data-testid={`video-card-${v.id}`}>
              <a href={v.youtube_url} target="_blank" rel="noopener noreferrer" className="block aspect-video bg-muted relative group">
                {v.thumbnail && (
                  <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" loading="lazy" />
                )}
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                  <ArrowSquareOut size={20} weight="duotone" className="text-white" />
                </div>
                {!v.published && (
                  <span className="absolute top-2 left-2 text-[9px] uppercase tracking-wider bg-yellow-600/90 text-black px-2 py-0.5">
                    Hidden
                  </span>
                )}
                <span className="absolute bottom-2 left-2 text-[10px] bg-black/70 text-white px-1.5 py-0.5">
                  #{v.sort_order}
                </span>
              </a>
              <div className="p-4 space-y-2">
                <div className="font-serif text-base leading-tight line-clamp-2" title={v.title}>{v.title}</div>
                {v.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{v.description}</p>
                )}
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground pt-1" data-testid={`video-stats-${v.id}`}>
                  <span title="Plays in last 30 days">▶ {analytics[v.id]?.window_plays ?? 0} this month</span>
                  <span className="opacity-60">·</span>
                  <span title="Lifetime plays">total {analytics[v.id]?.lifetime_plays ?? 0}</span>
                </div>
                <div className="flex justify-end gap-1 pt-2 border-t hairline">
                  <button
                    onClick={() => togglePublished(v)}
                    className="btn-ghost p-2 text-xs"
                    title={v.published ? "Hide from homepage" : "Show on homepage"}
                    data-testid={`video-toggle-${v.id}`}
                  >
                    {v.published ? <Eye size={14} /> : <EyeSlash size={14} />}
                  </button>
                  <button
                    onClick={() => setEditing(v)}
                    className="btn-ghost p-2 text-xs"
                    title="Edit"
                    data-testid={`video-edit-${v.id}`}
                  >
                    <PencilSimple size={14} />
                  </button>
                  <button
                    onClick={() => remove(v)}
                    className="btn-ghost p-2 text-xs hover:text-destructive"
                    title="Delete"
                    data-testid={`video-delete-${v.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <VideoEditModal
          video={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function VideoEditModal({ video, onClose, onSaved }) {
  const isNew = !video;
  const [form, setForm] = useState({
    title: video?.title || "",
    youtube_url: video?.youtube_url || "",
    description: video?.description || "",
    sort_order: video?.sort_order ?? 0,
    published: video?.published ?? true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.youtube_url.trim()) {
      toast.error("Title and YouTube URL are required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/landing-videos", form);
        toast.success("Added");
      } else {
        await api.put(`/admin/landing-videos/${video.id}`, form);
        toast.success("Saved");
      }
      onSaved();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10010] flex items-center justify-center p-4" data-testid="video-edit-modal">
      <button className="absolute inset-0 bg-black/70" onClick={onClose} aria-label="Close" />
      <form onSubmit={submit} className="relative card-flat w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="eyebrow">{isNew ? "Add video" : "Edit video"}</div>
        <label className="block">
          <span className="eyebrow block mb-1.5">Title *</span>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="90 seconds: How Estima decides Rent vs Buy"
            className="input-dark w-full px-3 py-2.5 text-sm"
            maxLength={160}
            required
            data-testid="video-title-input"
          />
          <div className="text-[10px] text-muted-foreground mt-1">{form.title.length}/160</div>
        </label>
        <label className="block">
          <span className="eyebrow block mb-1.5">YouTube URL *</span>
          <input
            value={form.youtube_url}
            onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
            placeholder="https://www.youtube.com/watch?v=… or https://youtu.be/…"
            className="input-dark w-full px-3 py-2.5 text-sm font-mono"
            required
            data-testid="video-url-input"
          />
          <div className="text-[10px] text-muted-foreground mt-1">
            Accepts watch URLs, short youtu.be links, /shorts/ and /embed/ forms.
          </div>
        </label>
        <label className="block">
          <span className="eyebrow block mb-1.5">Description (optional)</span>
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="One-line teaser shown under the title on the homepage"
            className="input-dark w-full px-3 py-2 text-sm"
            maxLength={400}
            data-testid="video-desc-input"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="eyebrow block mb-1.5">Sort order</span>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value || 0) })}
              className="input-dark w-full px-3 py-2 text-sm"
              data-testid="video-sort-input"
            />
            <div className="text-[10px] text-muted-foreground mt-1">Lower = first.</div>
          </label>
          <label className="flex items-center gap-2 text-sm pt-6">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              data-testid="video-published-input"
            />
            Published on homepage
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary px-4 py-2 text-sm" data-testid="video-save-btn">
            {saving ? "Saving…" : isNew ? "Add video" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
